import { Button, Dialog, IconClients } from "@dream/ui";
import { useNavigate } from "@tanstack/react-router";

/**
 * Primeiro uso do workspace.
 *
 * Sem cliente não dá para criar entrega, projeto nem link de acompanhamento.
 * O fluxo tribal (cliente → projeto → entrega → link) precisa aparecer na
 * cara de quem abre o calendário vazio — não só na página de projetos.
 */

export function WorkspaceSetupBanner() {
  const navigate = useNavigate();

  return (
    <aside
      role="status"
      className="dream-enter shrink-0 border-b border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-3 py-3 sm:px-4"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-[var(--dream-info-bg)] text-[var(--dream-info-fg)]">
            <IconClients className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[0.875rem] font-bold tracking-[0] text-[var(--color-ink)]">
              Cadastre o primeiro cliente para começar
            </p>
            <p className="mt-0.5 text-[0.8125rem] leading-5 text-[var(--color-ink-secondary)]">
              Entregas, projetos e o link de acompanhamento do cliente nascem a partir daqui. Sem
              cliente, o calendário não tem o que registrar.
            </p>
            <ol className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[0.75rem] font-medium text-[var(--color-ink-muted)]">
              <li>1. Cliente</li>
              <li>2. Projeto (opcional)</li>
              <li>3. Entrega</li>
              <li>4. Link para o cliente</li>
            </ol>
          </div>
        </div>
        <Button
          variant="primary"
          className="w-full shrink-0 sm:w-auto"
          onClick={() => void navigate({ to: "/clientes" })}
        >
          Cadastrar cliente
        </Button>
      </div>
    </aside>
  );
}

export function NeedClientDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Cadastre um cliente primeiro"
      description="Cada entrega pertence a um cliente. Sem esse cadastro, não há como agendar nem gerar o acompanhamento."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Agora não
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onOpenChange(false);
              void navigate({ to: "/clientes" });
            }}
          >
            Ir para clientes
          </Button>
        </>
      }
    >
      <ol className="space-y-2 text-[0.8125rem] leading-5 text-[var(--color-ink-secondary)]">
        <li>
          <span className="font-semibold text-[var(--color-ink)]">1. Cliente</span> — quem recebe o
          calendário.
        </li>
        <li>
          <span className="font-semibold text-[var(--color-ink)]">2. Projeto</span> — opcional, para
          agrupar campanhas.
        </li>
        <li>
          <span className="font-semibold text-[var(--color-ink)]">3. Entrega</span> — o que aparece
          no diário, na semana e na agenda.
        </li>
        <li>
          <span className="font-semibold text-[var(--color-ink)]">4. Link</span> — o cliente
          acompanha sem criar conta.
        </li>
      </ol>
    </Dialog>
  );
}
